import { darken, transparentize } from 'polished';
import React, { useContext } from 'react';
import styled, { css } from 'styled-components';
import { LFC, LegacyHTMLProps } from '../../Legacy';
import { Icon } from '../../concepts/resources/Icon';
import { Breakpoints } from '../../ui/Breakpoints';
import { Flexer } from '../dashboard/elements/LayoutElements';
import { Colors, toRem } from './../../ui';

const ItemIcon = styled.div`
    /* padding: .5rem;
    background: ${transparentize(.2, Colors.positive)};
    border-radius: .375rem; */
`;

const ItemLabel = styled.div`
    display: flex;
    align-items: center;
    font-size: 1rem;
    font-weight: 450;

    & svg, img {
        margin-right: .25em;
    }
`;

const ItemFrame = styled.div<{ width?: number | string, color?: string, disabled?: boolean, selected?: boolean }>`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 1rem;
    gap: .375rem;
    width: ${p => toRem(p.width ?? 'auto')};

    box-shadow: ${p => p.selected ? `0 0 0 .125rem ${Colors.primary}` : '0 4px 8px 0 rgb(0 0 0 / 4%)'};
    background: ${p => p.color ?? 'white'};
    color: ${p => !!p.color ? 'white' : p.selected ? Colors.primary : Colors.text};
    /* border: solid .0625rem ${Colors.border}; */
    border-radius: .25rem;
    cursor: pointer;
    user-select: none;

    &:hover { background: ${p => p.color ? darken(0.05, p.color) : 'white'} }
    &:focus { background: ${p => p.color ? darken(0.05, p.color) : 'white'} }
    &:active { background: ${p => p.color ? darken(0.10, p.color) : 'white'} }

    ${p => !p.disabled ? '' : css`
        opacity: .4;
        pointer-events: none !important;
        * { pointer-events: none !important; }
    `}
`;

const iconSizes = {
    reg: 3,
    min: 0,
};

const labelFontSizes = {
    reg: 1,
    min: .8125,
};

const ItemStrip = styled.div<{ bp: string }>`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 1rem;

    ${ItemFrame} {
        min-height: ${p => p.bp === 'reg' ? '7rem' : 'auto'};
    }

    ${ItemIcon} {
        svg, img {
            width: ${p => toRem(iconSizes[p.bp])}; 
            height: ${p => toRem(iconSizes[p.bp])}; 
        }
    }

    ${ItemLabel} {
        position: relative;
        font-size: ${p => toRem(labelFontSizes[p.bp])}; 

        svg, img {
            width: ${p => toRem(labelFontSizes[p.bp])}; 
            height: ${p => toRem(labelFontSizes[p.bp])}; 
        }
        
        ${p => p.bp === 'min' ? 'margin: 0;' : ''}
    }
`;

const ItemStripFrame = styled.div`
    padding: 1.5rem 2rem;
    background: ${Colors.hover};
`;

type ItemProps = { label: string, icon: string, color: string, disabled?: boolean } & React.HTMLAttributes<HTMLDivElement>;

const Button = React.memo<ItemProps>(({ label, icon, color, disabled, ...rest }) => {
    const bp = useContext(Breakpoints.Context);
    return <ItemFrame width="10" color={color} disabled={disabled} {...rest}>
        {bp === 'reg' && <ItemIcon>
            <Icon src={icon} />
        </ItemIcon>}
        <ItemLabel>
            <Icon src="deqo://icons/deqo/add" />
            <span>{label}</span>
        </ItemLabel>
    </ItemFrame>;
});

type BannerProps = { width?: number | string } & React.HTMLAttributes<HTMLDivElement>;

const Banner: LFC<BannerProps> = ({ width, children, ...rest }) => {
    return <ItemFrame width={width} color={Colors.primary} {...rest}>
        <Flexer flex="row" center gap=".5">
            {children}
        </Flexer>
    </ItemFrame>;
};

// const Help = styled.div`
//     margin-left: auto;
//     max-width: 25%;
//     display: flex;
//     align-items: center;
//     justify-content: center;
//     gap: 1rem;
//     padding: 1rem 1.5rem 1rem 1rem;
//     font-weight: 450;
//     background: ${Colors.mystery};
//     color: #fff;
//     border-radius: .5rem;
// `;

const HelpFrame = styled(ItemFrame)`
`;

const Help: LFC<LegacyHTMLProps<HTMLDivElement>> = ({ children, ...rest }) => {
    return <HelpFrame color={Colors.mystery} {...rest} >
        <Flexer flex="row" center gap=".5">
            {children}
        </Flexer>
    </HelpFrame>;
};

const Base: LFC = ({ children }) => {

    return <ItemStripFrame>
        <Breakpoints config={{ reg: 32, min: 0 }} render={bp =>
            <ItemStrip bp={bp}>
                {children}
            </ItemStrip>
        } />
    </ItemStripFrame>;
};

export const QuickMenuStrip = Object.assign(Base, { Button, Banner, Help });
